import { Page, PaginationParams } from '@/shared/api/pagination';
import type {
  CIHisItem,
  CIHisRequest,
  HISotDependsItem,
  HIChronologyResponse,
} from '../model/ci-his';
import type {
  TreeDownResponse,
  TreeLeafAlert,
  TreeRequest,
  TreeUpResponse,
} from '../model/tree';
import type { CISummary } from '../model/ci-summary';
import type {
  HIAnnotation,
  HIDataAlert,
  HIHistoryCIStatuses,
  HIHistoryLevel,
  HIHistoryLevelDown,
  HIHistoryLevelUp,
  HIHistoryValues,
  HIGroupHistoryRequest,
  HIGroupHistoryResponse,
  Params,
} from '../model/hi-history';
import { AxiosResponse, type AxiosRequestConfig } from 'axios';
import { monitoringInstance } from '@/shared/api/api-instances/monitoring/api-instance';

const baseURLOverride = {
  baseURL: '/api/monitoring/v4',
};

type AlertsCloseParams = {
  alertIds: string[];
  force?: boolean;
};

export type AlertCloseResponse = {
  id: string;
  ends_at: string;
};

export type AlertsCloseResponse = {
  summary: {
    requested: number;
    success: number;
    errors: number;
  };
  success: AlertCloseResponse[];
  errors: Array<{
    id: string;
    message: string;
  }>;
};

export class HIHistoryAPI {
  static getCIStatusesHistory(
    payload: { ci_ids: string[]; send_change?: boolean },
    options?: AxiosRequestConfig,
  ) {
    return monitoringInstance.post<HIHistoryCIStatuses>(
      `/hi_history/ci_statuses`,
      payload,
      { ...options, ...baseURLOverride },
    );
  }

  static getHighLevelHistory(params: Params, options?: AxiosRequestConfig) {
    return monitoringInstance.get<Page<HIHistoryLevel>>(
      '/hi_history/high_level',
      {
        ...options,
        ...baseURLOverride,
        params,
      },
    );
  }

  static getHighLevelHistoryPOST(
    ci_ids: string[],
    params: Params,
    options?: AxiosRequestConfig,
  ) {
    return monitoringInstance.post<Page<HIHistoryLevel>>(
      '/hi_history/high_level',
      ci_ids,
      {
        ...options,
        ...baseURLOverride,
        params,
      },
    );
  }

  static getHIHistoryValues(
    payload: { ci_ids: string[]; send_change?: boolean },
    options?: AxiosRequestConfig,
  ) {
    if (!payload?.ci_ids?.length)
      return Promise.resolve({
        data: { hi_values: [] },
      } as AxiosResponse<HIHistoryValues>);
    return monitoringInstance.post<HIHistoryValues>(
      '/hi_history/hi_values',
      payload,
      {
        ...options,
        ...baseURLOverride,
      },
    );
  }

  static getCalculatedHIHistory(
    payload: HIGroupHistoryRequest,
    options?: AxiosRequestConfig,
  ) {
    return monitoringInstance.post<HIGroupHistoryResponse>(
      '/hi_history/hi_group_history',
      payload,
      { ...options, ...baseURLOverride },
    );
  }

  static getHIHistoryLevelDown(
    params: PaginationParams & { sot_id: string },
    options?: AxiosRequestConfig,
  ) {
    return monitoringInstance.get<Page<HIHistoryLevelDown>>(
      '/hi_history/level_down',
      {
        ...options,
        ...baseURLOverride,
        params,
      },
    );
  }

  static getHIHistoryLevelUp(
    params: PaginationParams & { sot_id: string },
    options?: AxiosRequestConfig,
  ) {
    return monitoringInstance.get<Page<HIHistoryLevelUp>>(
      '/hi_history/level_up',
      {
        ...options,
        ...baseURLOverride,
        params,
      },
    );
  }

  static getSotById(
    sot_id: string,
    params?: { send_change?: boolean },
    options?: AxiosRequestConfig,
  ) {
    return monitoringInstance.get<HIHistoryLevel>('/hi_history/sot', {
      ...options,
      ...baseURLOverride,
      params: {
        sot_id,
        ...params,
      },
    });
  }

  static getAlertsBySotId(
    params: PaginationParams & { sot_id: string },
    options?: AxiosRequestConfig,
  ) {
    return monitoringInstance.get<Page<HIDataAlert>>('/hi_history/alerts', {
      ...options,
      ...baseURLOverride,
      params,
    });
  }

  static closeAlert({
    alertId,
    force = false,
  }: {
    alertId: string;
    force?: boolean;
  }) {
    return monitoringInstance.post<AlertCloseResponse>(
      `/alerts/${alertId}/close`,
      undefined,
      {
        ...baseURLOverride,
        params: { force },
      },
    );
  }

  static closeAlerts({ alertIds, force = false }: AlertsCloseParams) {
    return monitoringInstance.post<AlertsCloseResponse>(
      '/alerts/close',
      { alert_ids: alertIds, force },
      baseURLOverride,
    );
  }
  static getHIAnnotations(hi_id: string, options?: AxiosRequestConfig) {
    return monitoringInstance.get<{ annotations: HIAnnotation[] }>(
      '/hi_history/annotations',
      {
        ...options,
        ...baseURLOverride,
        params: { hi_id },
      },
    );
  }

  static getCISummary(
    payload: { ci_ids: string[] | null; severities?: number[] },
    params: PaginationParams & { started_at: string; finished_at?: string },
    options?: AxiosRequestConfig,
  ) {
    return monitoringInstance.post<Page<CISummary>>(
      '/hi_history/ci_summary',
      payload,
      { ...options, ...baseURLOverride, params },
    );
  }

  static getCIHis(
    params: CIHisRequest &
      PaginationParams & { started_at: string; finished_at?: string },
    options?: AxiosRequestConfig,
  ) {
    return monitoringInstance.get<Page<CIHisItem>>('/hi_history/ci_his', {
      ...options,
      ...baseURLOverride,
      params,
      // FastAPI list query params: severities=3&severities=4 (not severities[]=3)
      paramsSerializer: { indexes: null },
    });
  }

  static getHIChronology(
    params: PaginationParams & {
      ci_id: string;
      tql_hi_link_id: string;
      hi_type_name: string;
      started_at: string;
      finished_at?: string;
      send_change?: boolean;
    },
    options?: AxiosRequestConfig,
  ) {
    return monitoringInstance.get<HIChronologyResponse>(
      '/hi_history/hi_chronology',
      {
        ...options,
        ...baseURLOverride,
        params,
      },
    );
  }

  static getSotDepends(
    params: PaginationParams & {
      ci_id: string;
      tql_hi_link_id: string;
      hi_type_name: string;
      started_at: string;
      finished_at?: string;
    },
    options?: AxiosRequestConfig,
  ) {
    return monitoringInstance.get<Page<HISotDependsItem>>(
      '/hi_history/sot_depends',
      {
        ...options,
        ...baseURLOverride,
        params,
        // FastAPI list query params: severities=3&severities=4 (not severities[]=3)
        paramsSerializer: { indexes: null },
      },
    );
  }

  static getTreeDown(params: TreeRequest, options?: AxiosRequestConfig) {
    return monitoringInstance.get<TreeDownResponse>('/hi_history/tree_down', {
      ...options,
      ...baseURLOverride,
      params,
    });
  }

  static getTreeUp(params: TreeRequest, options?: AxiosRequestConfig) {
    return monitoringInstance.get<TreeUpResponse>('/hi_history/tree_up', {
      ...options,
      ...baseURLOverride,
      params,
    });
  }

  static getSotAlerts(
    params: PaginationParams & { sot_id: string; root_sot_id?: string },
    options?: AxiosRequestConfig,
  ) {
    return monitoringInstance.get<Page<TreeLeafAlert>>(
      '/hi_history/sot_alerts',
      { ...options, ...baseURLOverride, params },
    );
  }
}